/**
 * CoPilot Hooks
 *
 * React Query hooks for AI-powered guidance and recommendations.
 */

import { hashKey, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '../providers/SdkProvider';
import { heroesKeys } from './useHeroes';
import { checkUpKeys } from './useCheckUp';
import { careKeys } from './useCare';
import { qultureUpKeys } from './useQultureUp';
import { teleportalKeys } from './useTeleportal';
import {
  CoPilotService,
  CoPilotMessage,
  CoPilotConversationSummary,
  CoPilotSuggestion,
  CoPilotSuggestionId,
  CoPilotFeedbackInput,
  InteractInput,
  InteractResponse,
  CoPilotQuota,
  GuideStateInput,
  GuideStateResponse,
  GuideStateConflictSdkError,
} from '../services/copilot.service';

/**
 * Read the cached `rowVersion` for a (flowId, journeyId) tuple.
 *
 * The mutation hooks below auto-fill `expectedRowVersion` from this
 * helper so callers don't have to thread the token through manually.
 * If the row hasn't been fetched yet, returns undefined and the
 * server falls back to unchecked write semantics.
 */
function readCachedRowVersion(
  queryClient: ReturnType<typeof useQueryClient>,
  flowId: string,
  journeyId?: string
): number | undefined {
  const cached = queryClient.getQueryData<GuideStateResponse | null>(
    copilotKeys.guideState(flowId, journeyId)
  );
  return cached?.rowVersion;
}

/**
 * On a 409 conflict the cache is stale by definition — refetch the
 * row so the next mutation reads the fresh `rowVersion`.
 *
 * Returns the freshly-refetched rowVersion (or undefined if the GET
 * itself failed). Awaiting this guarantees `readCachedRowVersion` on
 * the next call sees post-refetch state — which is the fix for the
 * historic StrictMode/peer-tab `expected=N, actual=N+15` cascade
 * (radar 2026-05-08). The previous implementation invalidated and
 * returned a fire-and-forget promise, so a `setTimeout(250)` retry
 * could still race the in-flight GET on slow networks and re-send
 * the same stale rowVersion → conflict loop → permanent save gate.
 */
async function refetchRowVersion(
  queryClient: ReturnType<typeof useQueryClient>,
  flowId: string,
  journeyId?: string
): Promise<number | undefined> {
  await queryClient.refetchQueries({
    queryKey: copilotKeys.guideState(flowId, journeyId),
    exact: true,
  });
  return readCachedRowVersion(queryClient, flowId, journeyId);
}

/**
 * Patch ONLY the `rowVersion` on the cached guide-state row from a write's
 * response, SYNCHRONOUSLY (before the mutation's async `onSuccess` runs). This
 * closes the timing gap where a serialized next-write could start after the
 * previous write's `mutationFn` resolved but before its `onSuccess.setQueryData`
 * landed — reading a stale token. We preserve the rest of the cached state
 * (restart/retake/discard return only `{ ok, rowVersion }`, not a full row).
 */
function patchCachedRowVersion(
  queryClient: ReturnType<typeof useQueryClient>,
  flowId: string,
  journeyId: string | undefined,
  result: unknown
): void {
  const rowVersion = (result as { rowVersion?: number } | null | undefined)?.rowVersion;
  if (typeof rowVersion !== 'number') return;
  const key = copilotKeys.guideState(flowId, journeyId);
  const cached = queryClient.getQueryData<GuideStateResponse | null>(key);
  queryClient.setQueryData(
    key,
    cached ? { ...cached, rowVersion } : ({ rowVersion } as GuideStateResponse)
  );
}

/** One in-flight write chain per guide-state row — see `withConflictRetry`. */
const guideWriteChains = new Map<string, Promise<unknown>>();

async function attemptWrite<T>(
  queryClient: ReturnType<typeof useQueryClient>,
  flowId: string,
  journeyId: string | undefined,
  fn: (expectedRowVersion: number | undefined) => Promise<T>
): Promise<T> {
  const initial = readCachedRowVersion(queryClient, flowId, journeyId);
  try {
    const result = await fn(initial);
    patchCachedRowVersion(queryClient, flowId, journeyId, result);
    return result;
  } catch (err) {
    if (!(err instanceof GuideStateConflictSdkError)) throw err;
    const fresh = await refetchRowVersion(queryClient, flowId, journeyId);
    // If the refetch returned the SAME value we just sent, it's a real
    // server-side conflict (or the GET is broken) — bubble up rather
    // than hammer the endpoint with the same token.
    if (fresh === undefined || fresh === initial) throw err;
    const result = await fn(fresh);
    patchCachedRowVersion(queryClient, flowId, journeyId, result);
    return result;
  }
}

/**
 * Wrap a guide-state mutation that carries an `expectedRowVersion` so the shared
 * optimistic-concurrency token is safe against concurrent writers. Two layers:
 *
 *   1. SERIALIZE — all guide-state writes for a given (flowId, journeyId) run
 *      one-at-a-time through a module-level chain. Without this, overlapping
 *      writers (debounced autosave + finalize, or a StrictMode double-mount)
 *      each read the SAME cached rowVersion; the first wins (N→N+1) and every
 *      other 409s with `found=N+1` — a cascade the old retry-once couldn't
 *      converge, especially when the guide-state GET is slow (translate-on-read).
 *      Serializing means each write reads the token the previous write produced.
 *   2. RETRY-ONCE — on a genuine `GuideStateConflictSdkError` (a real peer tab /
 *      other browser), AWAIT a refetch, read the fresh rowVersion, and retry
 *      once; a second consecutive conflict surfaces to the host's error UI.
 *
 * The chain persists across mounts (module scope) so it also serializes across
 * StrictMode's mount/unmount/remount. A failed write never blocks the next.
 *
 * @internal Exported for unit tests only; call via the mutation hooks.
 */
export async function withConflictRetry<T>(
  queryClient: ReturnType<typeof useQueryClient>,
  flowId: string,
  journeyId: string | undefined,
  fn: (expectedRowVersion: number | undefined) => Promise<T>
): Promise<T> {
  const key = copilotKeys.guideState(flowId, journeyId).join(' ');
  const prev = guideWriteChains.get(key) ?? Promise.resolve();
  // Run after the previous write settles, regardless of its outcome.
  const run = prev.then(
    () => attemptWrite(queryClient, flowId, journeyId, fn),
    () => attemptWrite(queryClient, flowId, journeyId, fn)
  );
  const tail = run.catch(() => undefined);
  guideWriteChains.set(key, tail);
  void tail.then(() => {
    if (guideWriteChains.get(key) === tail) guideWriteChains.delete(key);
  });
  return run;
}

// ═══════════════════════════════════════════════════════════════════════
// QUERY KEYS
// ═══════════════════════════════════════════════════════════════════════

export const copilotKeys = {
  all: ['copilot'] as const,
  history: (params?: { limit?: number }) => [...copilotKeys.all, 'history', params] as const,
  suggestions: () => [...copilotKeys.all, 'suggestions'] as const,
  guides: (hero?: string) => [...copilotKeys.all, 'guides', hero] as const,
  quota: () => [...copilotKeys.all, 'quota'] as const,
  capabilities: () => [...copilotKeys.all, 'capabilities'] as const,
  guideState: (flowId: string, journeyId?: string) =>
    [...copilotKeys.all, 'guideState', flowId, journeyId ?? 'global'] as const,
  // Conversation history (0089). `conversations(query)` — the searchable list;
  // `conversation(id)` — one thread's messages.
  conversations: (query?: string) => [...copilotKeys.all, 'conversations', query ?? ''] as const,
  conversation: (id: string) => [...copilotKeys.all, 'conversations', id] as const,
};

// ═══════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Get chat history
 */
export function useCoPilotHistory(params?: { limit?: number; offset?: number }) {
  const client = useApiClient();
  const service = new CoPilotService(client);

  return useQuery<{ messages: CoPilotMessage[]; total: number }>({
    queryKey: copilotKeys.history(params),
    queryFn: () => service.getChatHistory(params),
  });
}

/**
 * List the user's conversations for the history panel (0089). `query` searches
 * titles + message bodies. `keepPreviousData` keeps the list steady while a new
 * search term resolves.
 */
export function useCoPilotConversations(params?: {
  query?: string;
  limit?: number;
  offset?: number;
}) {
  const client = useApiClient();
  const service = new CoPilotService(client);

  return useQuery<{ conversations: CoPilotConversationSummary[]; total: number }>({
    queryKey: copilotKeys.conversations(params?.query),
    queryFn: () => service.listConversations(params),
  });
}

/**
 * Load one conversation's messages (select-to-continue). Disabled until an id
 * is provided (a brand-new chat has none).
 */
export function useCoPilotConversationMessages(id?: string) {
  const client = useApiClient();
  const service = new CoPilotService(client);

  return useQuery<{ messages: CoPilotMessage[]; total: number }>({
    queryKey: copilotKeys.conversation(id ?? ''),
    queryFn: () => service.getConversationMessages(id as string),
    enabled: !!id,
  });
}

/**
 * Rename a conversation; refreshes the list + that thread.
 */
export function useRenameConversation() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();

  return useMutation<{ id: string; title: string }, Error, { id: string; title: string }>({
    mutationFn: ({ id, title }) => service.renameConversation(id, title),
    onSuccess: (_data, { id }) => {
      // 2-element prefix invalidates EVERY list (any search term) + the thread.
      queryClient.invalidateQueries({ queryKey: [...copilotKeys.all, 'conversations'] });
      queryClient.invalidateQueries({ queryKey: copilotKeys.conversation(id) });
    },
  });
}

/**
 * Soft-delete a conversation; refreshes the list.
 */
export function useDeleteConversation() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();

  return useMutation<{ success: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) => service.deleteConversation(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [...copilotKeys.all, 'conversations'] });
    },
  });
}

/**
 * Get proactive suggestions
 */
export function useCoPilotSuggestions() {
  const client = useApiClient();
  const service = new CoPilotService(client);

  return useQuery<CoPilotSuggestion[]>({
    queryKey: copilotKeys.suggestions(),
    queryFn: () => service.getSuggestions(),
    refetchInterval: (query) => (query.state.status === 'error' ? false : 5 * 60_000),
  });
}

/**
 * Submit feedback on CoPilot response
 */
export function useCoPilotFeedback() {
  const client = useApiClient();
  const service = new CoPilotService(client);

  return useMutation<{ success: boolean }, Error, CoPilotFeedbackInput>({
    mutationFn: (input) => service.submitFeedback(input),
  });
}

/**
 * Unified interact with CoPilot — routes to appropriate engine, returns CoPilotTrace
 */
export function useCoPilotInteract() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();

  return useMutation<InteractResponse, Error, InteractInput>({
    mutationFn: (input) => service.interact(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: copilotKeys.history({}) });
      queryClient.invalidateQueries({ queryKey: copilotKeys.quota() });
    },
  });
}

/**
 * Get server-side CoPilot quota. `enabled` lets a host defer the fetch until the
 * panel is actually opened — the engine is mounted at the app root now (0089),
 * so firing this at app mount races Clerk auth and 401s (leaving quota
 * undefined → the UI wrongly shows "0 remaining").
 */
export function useCoPilotQuota(options?: { enabled?: boolean }) {
  const client = useApiClient();
  const service = new CoPilotService(client);

  return useQuery<CoPilotQuota>({
    queryKey: copilotKeys.quota(),
    queryFn: () => service.getQuota(),
    enabled: options?.enabled ?? true,
    refetchInterval: (query) => (query.state.status === 'error' ? false : 2 * 60_000),
  });
}

/**
 * CoPilot per-capability kill-switch state (SR2 P1.2, TECH §14). The Companion
 * UI reads this to render degraded states honestly — hide the voice/upload
 * controls when a capability is switched off rather than letting them fail.
 * Cached for the session; capabilities change only on an operational flip.
 */
export function useCoPilotCapabilities(options?: { enabled?: boolean }) {
  const client = useApiClient();
  const service = new CoPilotService(client);

  return useQuery<Record<string, boolean>>({
    queryKey: copilotKeys.capabilities(),
    queryFn: () => service.getCapabilities(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60_000,
  });
}

/**
 * Guide-state cache-merge guard: keep the optimistic-concurrency token
 * MONOTONIC. The guide-state GET is translate-on-read and can take many
 * seconds; a slow fetch snapshots an OLD `rowVersion` and may resolve AFTER a
 * write already advanced the token. Returning `prev` here discards such a stale
 * result entirely (keeping the newer cached row) so it can't clobber the token
 * and 409 the next write.
 *
 * Safe because the server `rowVersion` ONLY ever increments — save/finalize AND
 * every reset (restart/retake/discard) do `rowVersion + 1` on the SAME row
 * (see guide-state.repository.ts), so a lower fetched value is ALWAYS a stale
 * slow-refetch, never a legitimate reset. Used as the query's
 * `structuralSharing`, which TanStack applies to both fetch results and
 * `setQueryData`.
 */
export function keepHigherRowVersion(
  prev: GuideStateResponse | null | undefined,
  next: GuideStateResponse | null | undefined
): GuideStateResponse | null | undefined {
  if (
    prev &&
    next &&
    typeof prev.rowVersion === 'number' &&
    typeof next.rowVersion === 'number' &&
    next.rowVersion < prev.rowVersion
  ) {
    return prev;
  }
  return next;
}

/**
 * Get persisted guide state for a flow, optionally scoped to a journey.
 */
export function useCoPilotGuideState(flowId: string, journeyId?: string) {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();

  return useQuery<GuideStateResponse | null>({
    queryKey: copilotKeys.guideState(flowId, journeyId),
    // A GET that lands while a save of this row is in flight read the row
    // BEFORE that save — the cache keeps showing what the save will write.
    queryFn: async () => {
      const row = await service.getGuideState(flowId, journeyId);
      const pending = latestPendingSave(queryClient, flowId, journeyId);
      return row && pending ? projectGuideStateSave(row, pending) : row;
    },
    enabled: !!flowId,
    retry: 1,
    retryDelay: 2000,
    staleTime: 1000 * 60,
    structuralSharing: (prev, next) =>
      keepHigherRowVersion(
        prev as GuideStateResponse | null | undefined,
        next as GuideStateResponse | null | undefined
      ) as typeof next,
  });
}

interface SaveGuideStateVariables {
  flowId: string;
  state: GuideStateInput;
  journeyId?: string;
}

/**
 * The guide-state saves in flight, per QueryClient and row, oldest first.
 *
 * Saves of one row are serialized (`withConflictRetry`) and each carries the
 * guide's WHOLE state, so the newest one is what the row will hold once they
 * land. Keyed by the QueryClient so two clients (two apps in one test) never
 * see each other's saves.
 */
const pendingGuideSaves = new WeakMap<
  ReturnType<typeof useQueryClient>,
  Map<string, SaveGuideStateVariables[]>
>();

function pendingSaveKey(flowId: string, journeyId: string | undefined): string {
  return hashKey(copilotKeys.guideState(flowId, journeyId));
}

function latestPendingSave(
  queryClient: ReturnType<typeof useQueryClient>,
  flowId: string,
  journeyId: string | undefined
): GuideStateInput | undefined {
  const saves = pendingGuideSaves.get(queryClient)?.get(pendingSaveKey(flowId, journeyId));
  return saves?.[saves.length - 1]?.state;
}

function trackPendingSave(
  queryClient: ReturnType<typeof useQueryClient>,
  variables: SaveGuideStateVariables
): void {
  let rows = pendingGuideSaves.get(queryClient);
  if (!rows) {
    rows = new Map();
    pendingGuideSaves.set(queryClient, rows);
  }
  const key = pendingSaveKey(variables.flowId, variables.journeyId);
  rows.set(key, [...(rows.get(key) ?? []), variables]);
}

function settlePendingSave(
  queryClient: ReturnType<typeof useQueryClient>,
  variables: SaveGuideStateVariables
): void {
  const rows = pendingGuideSaves.get(queryClient);
  const key = pendingSaveKey(variables.flowId, variables.journeyId);
  const left = (rows?.get(key) ?? []).filter((save) => save !== variables);
  if (left.length > 0) rows?.set(key, left);
  else rows?.delete(key);
}

/**
 * The guide-state row as it reads once `input` is saved over `row`.
 *
 * Mirrors the server's write (copilot.controller `saveGuideState` →
 * guide-state.repository `save`): every field the save carries replaces the
 * row's (a missing one is written as its empty value), the status is
 * `completed` or `active`, and a COMPLETED row is never reopened by an
 * autosave — only a blank reset (no chat, nothing completed) reopens it. The
 * row's identity, lifecycle counters and `rowVersion` are the server's and
 * stay as they are.
 *
 * @internal Exported for unit tests only.
 */
export function projectGuideStateSave(
  row: GuideStateResponse,
  input: GuideStateInput
): GuideStateResponse {
  const completedChats = input.completedChats ?? [];
  const status = input.status === 'completed' ? 'completed' : 'active';
  const isBlankReset = !input.currentChatId && completedChats.length === 0;
  if (row.status === 'completed' && status !== 'completed' && !isBlankReset) return row;
  return {
    ...row,
    currentTaskId: input.currentTaskId ?? null,
    currentChatId: input.currentChatId ?? null,
    completedTasks: input.completedTasks ?? [],
    completedChats,
    variables: input.variables ?? {},
    chatHistory: input.chatHistory ?? [],
    chatMessages: input.chatMessages ?? [],
    status,
    progress: input.progress ?? 0,
  };
}

/**
 * Save guide state for a flow.
 *
 * Optimistic-concurrency: auto-attaches the cached `rowVersion` to
 * `expectedRowVersion`; a stale-cache 409 is refetched and retried once by
 * `withConflictRetry`, and a second conflict reaches the caller.
 *
 * Optimistic cache: the cached row shows the save the moment it is made, not
 * when the server answers. The guide restores from this cache when it mounts,
 * so a remount while the save was in flight (a route change, a StrictMode
 * double-mount, the host re-keying the guide) used to restore the chat BEFORE
 * the save — the citizen was put back a step. A failed save puts the row back
 * as it was (unless a newer save already carries the whole state) and
 * refetches it; a success writes the server's row, with any newer save still
 * in flight laid over it.
 */
export function useSaveGuideState() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();

  return useMutation<
    GuideStateResponse,
    Error,
    SaveGuideStateVariables,
    { previous: GuideStateResponse | null | undefined }
  >({
    onMutate: (variables) => {
      const key = copilotKeys.guideState(variables.flowId, variables.journeyId);
      const previous = queryClient.getQueryData<GuideStateResponse | null>(key);
      trackPendingSave(queryClient, variables);
      // No row yet means nothing to restore either way: the server has none
      // until this first save lands, and a remount starts the guide afresh.
      if (previous) queryClient.setQueryData(key, projectGuideStateSave(previous, variables.state));
      return { previous };
    },
    mutationFn: ({ flowId, state, journeyId }) =>
      // `withConflictRetry` awaits a refetch and retries ONCE on a stale-
      // cache 409. If the caller passed an explicit `state.expectedRowVersion`
      // we honour it on the first attempt (caller-driven concurrency) but
      // still recover via the cached value on the retry. See helper docstring.
      withConflictRetry(queryClient, flowId, journeyId, (cached) =>
        service.saveGuideState(flowId, {
          ...state,
          expectedRowVersion: state.expectedRowVersion ?? cached,
        })
      ),
    retry: false,
    onSuccess: (data, variables) => {
      settlePendingSave(queryClient, variables);
      const newer = latestPendingSave(queryClient, variables.flowId, variables.journeyId);
      queryClient.setQueryData(
        copilotKeys.guideState(variables.flowId, variables.journeyId),
        newer ? projectGuideStateSave(data, newer) : data
      );
    },
    onError: (_error, variables, context) => {
      settlePendingSave(queryClient, variables);
      // A newer save carries the guide's whole state and owns the cache.
      if (latestPendingSave(queryClient, variables.flowId, variables.journeyId)) return;
      const key = copilotKeys.guideState(variables.flowId, variables.journeyId);
      const previous = context?.previous;
      if (previous) {
        // Back to the row as it was, at the token the cache holds now (a
        // conflict refetch may have advanced it — the token only increases).
        queryClient.setQueryData<GuideStateResponse | null>(key, (current) =>
          current ? { ...previous, rowVersion: current.rowVersion } : previous
        );
      }
      // The failure may be a peer's write or a lost connection: read the row
      // the server holds.
      void queryClient.invalidateQueries({ queryKey: key, exact: true });
    },
  });
}

// ────────────────────────────────────────────────────────────────────
// Lifecycle hooks — added 2026-04-25 for the FlowLifecycleManager.
// All five mutations invalidate the guide-state query on success so
// the manager re-fetches the canonical row from the server.
// ────────────────────────────────────────────────────────────────────

/** Mark resume of an active flow (idempotent within ~5min idle). */
export function useMarkGuideResume() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  return useMutation<
    { resumeCount: number; counted: boolean },
    Error,
    { flowId: string; journeyId?: string }
  >({
    mutationFn: ({ flowId, journeyId }) => service.markGuideResume(flowId, journeyId),
    retry: 1,
  });
}

/**
 * Cache invalidation set for the lifecycle mutations (restart /
 * retake / discard). Each of those endpoints atomically rewrites
 * BOTH the guide-state row AND the hero-side assessment row, so
 * every cache that reads either layer must refetch.
 *
 * Pre-existing bug (fixed 2026-04-27): we used to only invalidate
 * `heroesKeys.all = ['heroes']`, but the per-hero assessment caches
 * live under SIBLING namespaces — `['checkups']`, `['care']`,
 * `['qultureup']`, `['teleportal']` — none of which are children
 * of `['heroes']`, so the assessment query was never invalidated
 * after a discard. The host then re-read the stale
 * `assessmentStatus='in_progress'` from cache and bounced the user
 * back into the cancelled-and-wiped assessment URL.
 *
 * Cross-hero invalidation is cheap (the queries are small and the
 * user's tab tree usually only has one hero mounted), and far safer
 * than dispatching by `flowId` because the flow→hero mapping has
 * already drifted once and would drift again silently.
 */
function invalidateLifecycleCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  flowId: string,
  journeyId?: string
) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: copilotKeys.guideState(flowId, journeyId) }),
    queryClient.invalidateQueries({ queryKey: heroesKeys.all }),
    queryClient.invalidateQueries({ queryKey: checkUpKeys.all }),
    queryClient.invalidateQueries({ queryKey: careKeys.all }),
    queryClient.invalidateQueries({ queryKey: qultureUpKeys.all }),
    queryClient.invalidateQueries({ queryKey: teleportalKeys.all }),
  ]);
}

/**
 * Cache updates for a FINALIZE success. Unlike the reset mutations, finalize
 * RETURNS the authoritative finalized row (status='completed' + the fresh
 * rowVersion). We write that into the guide-state cache DIRECTLY rather than
 * invalidating → refetching it, because the guide-state GET is translate-on-read
 * and can take many seconds: a slow refetch snapshots an OLD rowVersion, resolves
 * late, and CLOBBERS the fresh token — 409-ing the NEXT flow's finalize (the bug
 * this fixes). Hero projections (CheckUp result, Care assessment, …) still
 * refetch so their homepages leave the "in progress" state.
 */
function finalizeSuccessCaches(
  queryClient: ReturnType<typeof useQueryClient>,
  flowId: string,
  journeyId: string | undefined,
  data: GuideStateResponse
) {
  queryClient.setQueryData(copilotKeys.guideState(flowId, journeyId), data);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: heroesKeys.all }),
    queryClient.invalidateQueries({ queryKey: checkUpKeys.all }),
    queryClient.invalidateQueries({ queryKey: careKeys.all }),
    queryClient.invalidateQueries({ queryKey: qultureUpKeys.all }),
    queryClient.invalidateQueries({ queryKey: teleportalKeys.all }),
  ]);
}

/**
 * Explicit destructive restart.
 *
 * `onSuccess` AWAITS the invalidation so `mutateAsync` does not resolve
 * until the guide-state cache holds the post-restart row. Without the
 * await, the host (`FlowLifecycleManager`) flips
 * `isResumeAcknowledged = true` and mounts `<CoPilotGuide>` while the
 * cache still serves the pre-restart state — the engine then restores
 * the conversation we just tried to wipe. Same applies to retake/discard.
 */
export function useRestartGuide() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();
  return useMutation<
    { ok: boolean; id?: string; restartCount?: number; rowVersion?: number },
    Error,
    { flowId: string; journeyId?: string; reason?: string }
  >({
    mutationFn: ({ flowId, journeyId, reason }) =>
      withConflictRetry(queryClient, flowId, journeyId, (cached) =>
        service.restartGuide(flowId, { reason, expectedRowVersion: cached }, journeyId)
      ),
    onSuccess: (_data, variables) =>
      invalidateLifecycleCaches(queryClient, variables.flowId, variables.journeyId),
  });
}

/** Retake an assessment — reset + hero-specific archive. */
export function useRetakeGuide() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();
  return useMutation<
    { ok: boolean; id?: string; retakeCount?: number; archived?: boolean; rowVersion?: number },
    Error,
    { flowId: string; journeyId?: string; reason?: string; archivePreviousResult?: boolean }
  >({
    mutationFn: ({ flowId, journeyId, reason, archivePreviousResult }) =>
      withConflictRetry(queryClient, flowId, journeyId, (cached) =>
        service.retakeGuide(
          flowId,
          { reason, archivePreviousResult, expectedRowVersion: cached },
          journeyId
        )
      ),
    onSuccess: (_data, variables) =>
      invalidateLifecycleCaches(queryClient, variables.flowId, variables.journeyId),
  });
}

/** Soft-cancel — marks status='cancelled' and wipes live-work fields. */
export function useDiscardGuide() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();
  return useMutation<
    { ok: boolean; id?: string; rowVersion?: number },
    Error,
    { flowId: string; journeyId?: string; reason?: string }
  >({
    mutationFn: ({ flowId, journeyId, reason }) =>
      withConflictRetry(queryClient, flowId, journeyId, (cached) =>
        service.discardGuide(flowId, { reason, expectedRowVersion: cached }, journeyId)
      ),
    onSuccess: (_data, variables) =>
      invalidateLifecycleCaches(queryClient, variables.flowId, variables.journeyId),
  });
}

/**
 * Atomic finalize — write final state + widget snapshot + flip status
 * to 'completed' in one DB transaction. Use this on `onComplete` from
 * `<CoPilotGuide>` instead of a save + multiple widget PATCHes + status
 * flip; the old fan-out could leave partially-finalized rows on a
 * network blip.
 */
export function useFinalizeGuide() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  const queryClient = useQueryClient();
  return useMutation<
    GuideStateResponse,
    Error,
    {
      flowId: string;
      journeyId?: string;
      currentTaskId?: string;
      currentChatId?: string;
      completedTasks?: string[];
      completedChats?: string[];
      variables?: Record<string, unknown>;
      chatHistory?: GuideStateResponse['chatHistory'];
      progress?: number;
      widgetStates?: Record<string, Record<string, unknown>>;
    }
  >({
    mutationFn: ({ flowId, journeyId, ...body }) =>
      // Same await-refetch-and-retry-once semantics as useSaveGuideState.
      // Critical for the recap_summary submit path: the engine's
      // `saveCompletedState()` and the host's `finalize()` used to fire
      // back-to-back against the same cached rowVersion, so whichever
      // landed second saw `expected=N, actual=N+1` and threw — leaving
      // the user staring at "Could not save your results" after a
      // 15-minute assessment. `withConflictRetry` recovers transparently.
      withConflictRetry(queryClient, flowId, journeyId, (cached) =>
        service.finalizeGuideState(flowId, {
          ...body,
          journeyInstanceId: journeyId,
          expectedRowVersion: cached,
        })
      ),
    // Finalize is the success peer of restart / retake / discard; share
    // the same cache-invalidation contract so the post-finalize render
    // sees the row's `status='completed'` AND any hero-side projection
    // (CheckUp result, Care assessment, QultureUp level, TelePortal kit)
    // refreshes without per-host plumbing. Pre-2026-04-27 only the
    // guide-state cache invalidated here, which left hero homepages
    // showing stale "in progress" cards on a flow that had just
    // completed — only the hosts that hand-rolled their own
    // `afterFinalize` invalidation (CheckUp, Care) were correct.
    onSuccess: (data, variables) =>
      finalizeSuccessCaches(queryClient, variables.flowId, variables.journeyId, data),
    // No onError invalidation: withConflictRetry already refetched.
  });
}

/** Patch a single widget's persistent state slice. */
export function usePatchGuideWidgetState() {
  const client = useApiClient();
  const service = new CoPilotService(client);
  // No queryClient — caller (useWidgetState) already holds optimistic
  // local state. Invalidating the guide-state query on every widget
  // patch would thrash the UI; we let it refresh on its own staleTime.
  return useMutation<
    { ok: boolean; widgetStates?: Record<string, Record<string, unknown>> },
    Error,
    {
      flowId: string;
      journeyId?: string;
      widgetKey: string;
      partial: Record<string, unknown>;
    }
  >({
    mutationFn: ({ flowId, journeyId, widgetKey, partial }) =>
      service.patchGuideWidgetState(flowId, { widgetKey, partial }, journeyId),
    // No invalidate — the caller (useWidgetState) holds optimistic local
    // state. We only refresh the guide-state query opportunistically when
    // it gets stale on its own.
    retry: 1,
  });
}

// Re-export types
export type {
  CoPilotMessage,
  CoPilotSuggestion,
  CoPilotSuggestionId,
  CoPilotFeedbackInput,
  InteractInput,
  InteractResponse,
  CoPilotQuota,
  GuideStateInput,
  GuideStateResponse,
};

// Re-export the typed conflict error so consumers can instanceof-check.
export { GuideStateConflictSdkError } from '../services/copilot.service';
